const CUSTOM_CUT_MAX_PER_SIDE = 48;
const CUSTOM_CUT_BARRIER_WIDTH_PX = 2;
const CUSTOM_CUT_ALPHA_THRESHOLD = 18;

const imagePromiseCache = new Map();
const imageElementCache = new Map();

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const normalizeText = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

export const normalizeCustomCutPoint = (point = []) => ([
    clamp(Number(point?.[0] || 0), 0, 1),
    clamp(Number(point?.[1] || 0), 0, 1),
]);

export const normalizeCustomCutStroke = (cut = {}) => {
    const points = Array.isArray(cut?.points_norm)
        ? cut.points_norm.map((point) => normalizeCustomCutPoint(point))
        : [];
    const stagePoints = Array.isArray(cut?.stage_points_norm)
        ? cut.stage_points_norm.map((point) => normalizeCustomCutPoint(point))
        : [];
    const rawTargetGroup = normalizeText(cut?.target_group);
    const targetGroup = rawTargetGroup === "sleeve" || rawTargetGroup === "body" ? rawTargetGroup : "free";
    const rawTool = normalizeText(cut?.tool);
    const tool = rawTool === "curve" ? "curve" : "line";
    const rawAnchorComponent = normalizeText(cut?.anchor_component);
    const anchorComponent = rawAnchorComponent === "right" ? "right" : "left";
    const rawMirroredFromSide = normalizeText(cut?.mirrored_from_side);
    const mirroredFromSide = rawMirroredFromSide === "back" || rawMirroredFromSide === "front"
        ? rawMirroredFromSide
        : null;
    const resolvedId = String(cut?.id || `cut-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const mirrorGroupId = String(cut?.mirror_group_id || resolvedId);
    const affectedParts = Array.isArray(cut?.affected_parts)
        ? cut.affected_parts.map((value) => String(value)).filter(Boolean)
        : [];
    return {
        ...cut,
        id: resolvedId,
        tool,
        target_group: targetGroup,
        points_norm: points,
        stage_points_norm: stagePoints,
        mirror_mode: "paired-sides",
        mirror_group_id: mirrorGroupId,
        mirrored_from_side: mirroredFromSide,
        anchor_component: targetGroup === "sleeve" ? anchorComponent : "body",
        affected_parts: affectedParts,
    };
};

export const buildMirroredCustomCutState = (sourceSide, cuts, previousState = null) => {
    const safeSourceSide = sourceSide === "back" ? "back" : "front";
    const oppositeSide = safeSourceSide === "front" ? "back" : "front";
    const normalized = (Array.isArray(cuts) ? cuts : [])
        .map((cut) => normalizeCustomCutStroke(cut))
        .filter((cut) => Array.isArray(cut.points_norm) && cut.points_norm.length >= 2)
        .slice(0, CUSTOM_CUT_MAX_PER_SIDE);

    const mirrored = normalized
        .filter((cut) => !cut.mirrored_from_side)
        .map((cut) => ({
            ...cut,
            mirrored_from_side: safeSourceSide,
            mirror_group_id: String(cut?.mirror_group_id || cut?.id || ""),
        }));

    const mirroredIds = new Set(mirrored.map((cut) => String(cut?.id || "")));
    const previousOpposite = (Array.isArray(previousState?.[oppositeSide]) ? previousState[oppositeSide] : [])
        .map((cut) => normalizeCustomCutStroke(cut))
        .filter((cut) => Array.isArray(cut.points_norm) && cut.points_norm.length >= 2);
    const preservedOpposite = previousOpposite.filter((cut) => {
        if (String(cut?.mirrored_from_side || "") === safeSourceSide) return false;
        if (mirroredIds.has(String(cut?.id || ""))) return false;
        return true;
    });

    const nextOpposite = [...preservedOpposite, ...mirrored].slice(0, CUSTOM_CUT_MAX_PER_SIDE);
    return safeSourceSide === "front"
        ? { front: normalized, back: nextOpposite }
        : { front: nextOpposite, back: normalized };
};

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

export const loadCachedCanvasImage = async (src) => {
    if (!src) return null;
    if (imageElementCache.has(src)) return imageElementCache.get(src);
    if (!imagePromiseCache.has(src)) {
        imagePromiseCache.set(
            src,
            loadImage(src)
                .then((image) => {
                    imageElementCache.set(src, image);
                    imagePromiseCache.delete(src);
                    return image;
                })
                .catch((error) => {
                    imagePromiseCache.delete(src);
                    throw error;
                })
        );
    }
    try {
        return await imagePromiseCache.get(src);
    } catch (_error) {
        return null;
    }
};

const drawContainImage = (ctx, img, width, height) => {
    if (!ctx || !img || !width || !height) return;
    const srcWidth = img.naturalWidth || img.width || 1;
    const srcHeight = img.naturalHeight || img.height || 1;
    const scale = Math.min(width / srcWidth, height / srcHeight);
    const drawWidth = srcWidth * scale;
    const drawHeight = srcHeight * scale;
    const dx = (width - drawWidth) / 2;
    const dy = (height - drawHeight) / 2;
    ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
};

const BODY_INCLUDE_PATTERNS = [
    "cuerpo",
    "body",
    "torso",
    "panel",
    "delantero",
    "delantera",
    "espalda",
    "trasero",
    "trasera",
    "pecho",
];

const BODY_EXCLUDE_PATTERNS = [
    "manga",
    "sleeve",
    "capucha",
    "hood",
    "cuello",
    "collar",
    "puno",
    "puño",
    "cintura",
    "waist",
    "tira",
    "serpentin",
    "recort",
    "extra",
    "bolsillo",
    "pocket",
];

const SLEEVE_PATTERNS = ["manga", "sleeve"];

const resolveLayerLabel = (layer = null) =>
    normalizeText(
        layer?.partLabel
        || layer?.item?.factory_name
        || layer?.item?.nname
        || layer?.item?.name
        || layer?.node?.name
        || ""
    );

const resolveLayerCutGroup = (layer = null) => {
    const label = resolveLayerLabel(layer);
    if (!label) return null;
    if (SLEEVE_PATTERNS.some((pattern) => label.includes(pattern))) {
        return "sleeve";
    }
    if (
        BODY_INCLUDE_PATTERNS.some((pattern) => label.includes(pattern))
        && !BODY_EXCLUDE_PATTERNS.some((pattern) => label.includes(pattern))
    ) {
        return "body";
    }
    return null;
};

const createAlphaCanvas = (width, height) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, width);
    canvas.height = Math.max(1, height);
    return canvas;
};

const computeAlphaMetrics = (alpha, width, height, threshold = CUSTOM_CUT_ALPHA_THRESHOLD) => {
    if (!alpha?.length || width <= 1 || height <= 1) return null;
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    let pixelCount = 0;
    let sumX = 0;
    let sumY = 0;
    for (let index = 0; index < alpha.length; index += 1) {
        if (alpha[index] <= threshold) continue;
        const x = index % width;
        const y = Math.floor(index / width);
        pixelCount += 1;
        sumX += x;
        sumY += y;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
    }
    if (!pixelCount || maxX < minX || maxY < minY) return null;
    return {
        pixelCount,
        bbox: {
            minX,
            minY,
            maxX,
            maxY,
            width: Math.max(1, (maxX - minX) + 1),
            height: Math.max(1, (maxY - minY) + 1),
        },
        centroid: {
            x: sumX / pixelCount,
            y: sumY / pixelCount,
        },
    };
};

const extractAlphaMap = (ctx, width, height, threshold = CUSTOM_CUT_ALPHA_THRESHOLD) => {
    const data = ctx.getImageData(0, 0, width, height).data;
    const alpha = new Uint8ClampedArray(width * height);
    for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
        alpha[p] = data[i + 3] > threshold ? 255 : 0;
    }
    return alpha;
};

const computeConnectedComponents = ({ alpha, width, height, minPixelCount = 80 }) => {
    if (!alpha?.length || width <= 1 || height <= 1) return [];
    const visited = new Uint8Array(width * height);
    const components = [];
    const queue = new Int32Array(width * height);
    const pixels = [];

    const flood = (startIndex) => {
        let head = 0;
        let tail = 0;
        queue[tail++] = startIndex;
        visited[startIndex] = 1;
        let minX = width;
        let minY = height;
        let maxX = 0;
        let maxY = 0;
        let pixelCount = 0;
        let sumX = 0;
        let sumY = 0;
        pixels.length = 0;

        while (head < tail) {
            const index = queue[head++];
            pixels.push(index);
            pixelCount += 1;
            const x = index % width;
            const y = Math.floor(index / width);
            sumX += x;
            sumY += y;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;

            const leftNeighbor = index - 1;
            const rightNeighbor = index + 1;
            const topNeighbor = index - width;
            const bottomNeighbor = index + width;
            if (x > 0 && !visited[leftNeighbor] && alpha[leftNeighbor] > 0) {
                visited[leftNeighbor] = 1;
                queue[tail++] = leftNeighbor;
            }
            if (x < width - 1 && !visited[rightNeighbor] && alpha[rightNeighbor] > 0) {
                visited[rightNeighbor] = 1;
                queue[tail++] = rightNeighbor;
            }
            if (y > 0 && !visited[topNeighbor] && alpha[topNeighbor] > 0) {
                visited[topNeighbor] = 1;
                queue[tail++] = topNeighbor;
            }
            if (y < height - 1 && !visited[bottomNeighbor] && alpha[bottomNeighbor] > 0) {
                visited[bottomNeighbor] = 1;
                queue[tail++] = bottomNeighbor;
            }
        }

        if (pixelCount < minPixelCount) return null;
        const componentAlpha = new Uint8ClampedArray(width * height);
        pixels.forEach((index) => {
            componentAlpha[index] = 255;
        });
        return {
            alpha: componentAlpha,
            pixelCount,
            bbox: {
                minX,
                minY,
                maxX,
                maxY,
                width: Math.max(1, (maxX - minX) + 1),
                height: Math.max(1, (maxY - minY) + 1),
            },
            centroid: {
                x: pixelCount > 0 ? (sumX / pixelCount) : 0,
                y: pixelCount > 0 ? (sumY / pixelCount) : 0,
            },
        };
    };

    for (let index = 0; index < alpha.length; index += 1) {
        if (visited[index] || alpha[index] <= 0) continue;
        const component = flood(index);
        if (component) components.push(component);
    }
    return components;
};

const mapRelativePointsToBBox = (points = [], bbox, mirrorX = false, width = 1, height = 1) => {
    if (!Array.isArray(points) || !bbox) return [];
    return points.map((point) => {
        const px = clamp(Number(point?.[0] || 0), 0, 1);
        const py = clamp(Number(point?.[1] || 0), 0, 1);
        const normalizedX = mirrorX ? (1 - px) : px;
        const x = bbox.minX + (normalizedX * bbox.width);
        const y = bbox.minY + (py * bbox.height);
        return [
            clamp(x / width, 0, 1),
            clamp(y / height, 0, 1),
        ];
    });
};

const mirrorGlobalPoints = (points = []) =>
    (Array.isArray(points) ? points : []).map((point) => {
        const px = clamp(Number(point?.[0] || 0), 0, 1);
        const py = clamp(Number(point?.[1] || 0), 0, 1);
        return [1 - px, py];
    });

const drawPathFromPoints = (ctx, points = [], width, height) => {
    if (!ctx || !Array.isArray(points) || points.length < 2) return;
    const start = points[0];
    ctx.beginPath();
    ctx.moveTo(clamp(Number(start?.[0] || 0) * width, 0, width), clamp(Number(start?.[1] || 0) * height, 0, height));
    for (let index = 1; index < points.length; index += 1) {
        const point = points[index];
        ctx.lineTo(
            clamp(Number(point?.[0] || 0) * width, 0, width),
            clamp(Number(point?.[1] || 0) * height, 0, height)
        );
    }
};

export const drawCustomCutStroke = (
    ctx,
    cut,
    width,
    height,
    {
        color = "rgba(76, 85, 99, 0.9)",
        lineWidth = CUSTOM_CUT_BARRIER_WIDTH_PX,
        dashed = true,
    } = {}
) => {
    if (!ctx || !cut || width <= 1 || height <= 1) return;
    const points = Array.isArray(cut?.points_norm) ? cut.points_norm : [];
    if (points.length < 2) return;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, Number(lineWidth || CUSTOM_CUT_BARRIER_WIDTH_PX));
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    if (dashed) ctx.setLineDash([3, 7]);
    drawPathFromPoints(ctx, points, width, height);
    ctx.stroke();
    ctx.restore();
};

const buildBarrierAlpha = ({ cuts = [], width, height, componentAlpha = null }) => {
    if (!cuts.length || width <= 1 || height <= 1 || typeof document === "undefined") {
        return new Uint8ClampedArray(width * height);
    }
    const canvas = createAlphaCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return new Uint8ClampedArray(width * height);
    cuts.forEach((cut) => {
        ctx.save();
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = Math.max(4, Number(cut?.lineWidth || CUSTOM_CUT_BARRIER_WIDTH_PX));
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        drawPathFromPoints(ctx, cut.points_norm, width, height);
        ctx.stroke();
        ctx.restore();
    });
    let alpha = extractAlphaMap(ctx, width, height, 8);
    if (componentAlpha?.length) {
        for (let index = 0; index < alpha.length; index += 1) {
            if (!componentAlpha[index]) alpha[index] = 0;
        }
    }
    return alpha;
};

const buildComponentZoneMasks = ({
    component,
    transformedCuts = [],
    width,
    height,
    minZonePixelCount = 140,
}) => {
    if (!component?.alpha?.length || width <= 1 || height <= 1) return [];
    const barrierAlpha = buildBarrierAlpha({
        cuts: transformedCuts,
        width,
        height,
        componentAlpha: component.alpha,
    });
    const availableAlpha = new Uint8ClampedArray(width * height);
    for (let index = 0; index < availableAlpha.length; index += 1) {
        availableAlpha[index] = component.alpha[index] > 0 && barrierAlpha[index] <= 0 ? 255 : 0;
    }
    return computeConnectedComponents({
        alpha: availableAlpha,
        width,
        height,
        minPixelCount: minZonePixelCount,
    });
};

const getZoneSortScore = (zone) => {
    const bbox = zone?.bbox || {};
    const centroid = zone?.centroid || {};
    return {
        y: Number(centroid.y ?? bbox.minY ?? 0),
        x: Number(centroid.x ?? bbox.minX ?? 0),
    };
};

const sortZonesTopToBottom = (zones = []) =>
    [...zones].sort((left, right) => {
        const a = getZoneSortScore(left);
        const b = getZoneSortScore(right);
        if (Math.abs(a.y - b.y) > 8) return a.y - b.y;
        return a.x - b.x;
    });

const expandZoneAlpha = (alpha, width, height, radius = 1) => {
    const safeRadius = Math.max(0, Math.round(Number(radius || 0)));
    if (!alpha?.length || width <= 1 || height <= 1 || safeRadius <= 0) return alpha;
    let expanded = new Uint8ClampedArray(alpha);
    for (let pass = 0; pass < safeRadius; pass += 1) {
        const next = new Uint8ClampedArray(expanded);
        for (let y = 0; y < height; y += 1) {
            for (let x = 0; x < width; x += 1) {
                const index = (y * width) + x;
                if (expanded[index] > 0) continue;
                let found = false;
                for (let oy = -1; oy <= 1 && !found; oy += 1) {
                    const ny = y + oy;
                    if (ny < 0 || ny >= height) continue;
                    for (let ox = -1; ox <= 1; ox += 1) {
                        const nx = x + ox;
                        if (nx < 0 || nx >= width) continue;
                        if (expanded[(ny * width) + nx] > 0) {
                            next[index] = 255;
                            found = true;
                            break;
                        }
                    }
                }
            }
        }
        expanded = next;
    }
    return expanded;
};

const buildZoneCanvas = (zone, width, height) => {
    if (!zone?.alpha?.length || width <= 1 || height <= 1 || typeof document === "undefined") return null;
    const canvas = createAlphaCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const imageData = ctx.createImageData(width, height);
    const visualAlpha = expandZoneAlpha(
        zone.alpha,
        width,
        height,
        Math.max(1, Math.round(CUSTOM_CUT_BARRIER_WIDTH_PX / 2.5))
    );
    for (let index = 0; index < visualAlpha.length; index += 1) {
        const alpha = visualAlpha[index];
        if (!alpha) continue;
        imageData.data[(index * 4) + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
};

const sampleCutPixels = (cut, width, height, stepPx = 4) => {
    const points = Array.isArray(cut?.points_norm) ? cut.points_norm : [];
    if (points.length < 2 || width <= 1 || height <= 1) return [];
    const samples = [];
    for (let index = 1; index < points.length; index += 1) {
        const start = points[index - 1] || [];
        const end = points[index] || [];
        const ax = clamp(Number(start[0] || 0) * (width - 1), 0, width - 1);
        const ay = clamp(Number(start[1] || 0) * (height - 1), 0, height - 1);
        const bx = clamp(Number(end[0] || 0) * (width - 1), 0, width - 1);
        const by = clamp(Number(end[1] || 0) * (height - 1), 0, height - 1);
        const distance = Math.hypot(bx - ax, by - ay);
        const steps = Math.max(1, Math.round(distance / Math.max(1, Number(stepPx || 4))));
        for (let step = 0; step <= steps; step += 1) {
            const t = step / steps;
            samples.push([
                clamp(Math.round(ax + ((bx - ax) * t)), 0, width - 1),
                clamp(Math.round(ay + ((by - ay) * t)), 0, height - 1),
            ]);
        }
    }
    return samples;
};

const resolveAdjacentCutIdsForZone = ({
    zone = null,
    renderedCuts = [],
    width = 0,
    height = 0,
    radius = 3,
}) => {
    if (!zone?.alpha?.length || !Array.isArray(renderedCuts) || !renderedCuts.length || width <= 1 || height <= 1) {
        return [];
    }
    const expandedAlpha = expandZoneAlpha(zone.alpha, width, height, radius);
    const adjacent = new Set();
    renderedCuts.forEach((cut) => {
        const cutId = String(cut?.id || "").trim();
        if (!cutId) return;
        const samples = sampleCutPixels(cut, width, height, 4);
        const touchesZone = samples.some(([x, y]) => expandedAlpha[(y * width) + x] > 0);
        if (touchesZone) {
            adjacent.add(cutId);
        }
    });
    return Array.from(adjacent);
};

const resolveZonePartKeys = (zone, partGeometries = []) => {
    if (!zone?.alpha?.length || !Array.isArray(partGeometries) || !partGeometries.length) return [];
    const overlaps = [];
    partGeometries.forEach((geometry) => {
        const key = String(geometry?.layer?.partKey || "");
        const sourceAlpha = geometry?.alpha;
        if (!key || !sourceAlpha?.length) return;
        let overlap = 0;
        for (let index = 0; index < zone.alpha.length; index += 1) {
            if (zone.alpha[index] > 0 && sourceAlpha[index] > 0) {
                overlap += 1;
            }
        }
        if (overlap < 18) return;
        overlaps.push({
            key,
            overlap,
            zIndex: Number(geometry?.layer?.zIndex || 0),
        });
    });
    if (!overlaps.length) return [];
    const minZ = overlaps.reduce((lowest, entry) => Math.min(lowest, entry.zIndex), overlaps[0].zIndex);
    const eligible = overlaps.filter((entry) => entry.zIndex <= (minZ + 2));
    return Array.from(new Set(eligible.map((entry) => entry.key)));
};

const resolveStoredColor = (colorValue = null) => {
    if (!colorValue) return null;
    if (typeof colorValue === "object") {
        return {
            ...colorValue,
            id: colorValue.id ?? colorValue.color_id ?? null,
            color_id: colorValue.color_id ?? colorValue.id ?? null,
            code: String(colorValue.code || colorValue.hex || "").trim(),
            name: String(colorValue.name || colorValue.label || "Color"),
        };
    }
    const code = String(colorValue).trim();
    if (!code) return null;
    return { id: null, color_id: null, code, name: code };
};

const buildRenderedCut = ({
    cut,
    side,
    groupKey,
    componentKey,
    component,
    width,
    height,
    mirrorX = false,
}) => ({
    ...cut,
    side,
    groupKey,
    componentKey,
    points_norm: Array.isArray(cut?.stage_points_norm) && cut.stage_points_norm.length >= 2
        ? (mirrorX ? mirrorGlobalPoints(cut.stage_points_norm) : cut.stage_points_norm)
        : mapRelativePointsToBBox(cut.points_norm, component.bbox, mirrorX, width, height),
    lineWidth: CUSTOM_CUT_BARRIER_WIDTH_PX,
});

const doesStrokeIntersectComponent = ({
    points = [],
    component = null,
    width = 0,
    height = 0,
}) => {
    if (!component?.alpha?.length || !Array.isArray(points) || points.length < 2 || width <= 1 || height <= 1) {
        return false;
    }
    const alpha = component.alpha;
    const isInside = (nx, ny) => {
        const x = clamp(Math.round(Number(nx || 0) * (width - 1)), 0, width - 1);
        const y = clamp(Math.round(Number(ny || 0) * (height - 1)), 0, height - 1);
        return alpha[(y * width) + x] > 0;
    };
    for (let index = 1; index < points.length; index += 1) {
        const a = points[index - 1];
        const b = points[index];
        const steps = Math.max(12, Math.round(Math.hypot(
            (Number(b?.[0] || 0) - Number(a?.[0] || 0)) * width,
            (Number(b?.[1] || 0) - Number(a?.[1] || 0)) * height
        ) / 5));
        for (let step = 0; step <= steps; step += 1) {
            const t = step / steps;
            const nx = Number(a?.[0] || 0) + ((Number(b?.[0] || 0) - Number(a?.[0] || 0)) * t);
            const ny = Number(a?.[1] || 0) + ((Number(b?.[1] || 0) - Number(a?.[1] || 0)) * t);
            if (isInside(nx, ny)) return true;
        }
    }
    return false;
};

const buildBodyRuntime = ({
    bodyComponent,
    side,
    cuts,
    partKeys = [],
    width,
    height,
    customCutZoneColors,
}) => {
    if (!bodyComponent || !cuts.length) {
        return { zones: [], renderedCuts: [], component: bodyComponent || null };
    }
    const renderedCuts = cuts
        .map((cut) =>
            buildRenderedCut({
                cut,
                side,
                groupKey: "body",
                componentKey: "body",
                component: bodyComponent,
                width,
                height,
                mirrorX: false,
            })
        )
        .filter((cut) => doesStrokeIntersectComponent({
            points: cut.points_norm,
            component: bodyComponent,
            width,
            height,
        }));
    const rawZones = buildComponentZoneMasks({
        component: bodyComponent,
        transformedCuts: renderedCuts,
        width,
        height,
    });
    const zones = sortZonesTopToBottom(rawZones).map((zone, index) => {
        const id = `body-${side}-zone-${index + 1}`;
        return {
            ...zone,
            id,
            side,
            targetGroup: "body",
            componentKey: "body",
            partKeys,
            label: rawZones.length === 2
                ? (index === 0 ? "Superior" : "Inferior")
                : `Zona ${index + 1}`,
            adjacentCutIds: resolveAdjacentCutIdsForZone({
                zone,
                renderedCuts,
                width,
                height,
            }),
            color: resolveStoredColor(customCutZoneColors?.[id]),
            maskCanvas: buildZoneCanvas(zone, width, height),
        };
    });
    return {
        component: bodyComponent,
        zones,
        renderedCuts,
    };
};

const buildSleeveRuntime = ({
    sleeveComponents = [],
    side,
    cuts,
    partKeys = [],
    width,
    height,
    customCutZoneColors,
}) => {
    if (!sleeveComponents.length || !cuts.length) {
        return { components: sleeveComponents, zones: [], renderedCuts: [] };
    }
    const renderedCuts = [];
    const zones = [];

    sleeveComponents.forEach((component) => {
        const componentCuts = cuts
            .map((cut) => {
                const primary = buildRenderedCut({
                    cut,
                    side,
                    groupKey: "sleeve",
                    componentKey: component.slot,
                    component,
                    width,
                    height,
                    mirrorX: false,
                });
                if (doesStrokeIntersectComponent({
                    points: primary.points_norm,
                    component,
                    width,
                    height,
                })) {
                    return primary;
                }
                return null;
            })
            .filter(Boolean);
        renderedCuts.push(...componentCuts);
        const rawZones = buildComponentZoneMasks({
            component,
            transformedCuts: componentCuts,
            width,
            height,
        });
        sortZonesTopToBottom(rawZones).forEach((zone, index) => {
            const id = `sleeve-${component.slot}-${side}-zone-${index + 1}`;
            zones.push({
                ...zone,
                id,
                side,
                targetGroup: "sleeve",
                componentKey: component.slot,
                partKeys,
                label: `${component.slot === "right" ? "Manga derecha" : "Manga izquierda"} · Zona ${index + 1}`,
                adjacentCutIds: resolveAdjacentCutIdsForZone({
                    zone,
                    renderedCuts: componentCuts,
                    width,
                    height,
                }),
                color: resolveStoredColor(customCutZoneColors?.[id]),
                maskCanvas: buildZoneCanvas(zone, width, height),
            });
        });
    });

    return {
        components: sleeveComponents,
        zones,
        renderedCuts,
    };
};

const buildGarmentRuntime = ({
    garmentComponents = [],
    side,
    cuts,
    partGeometries = [],
    width,
    height,
    customCutZoneColors,
}) => {
    const runtimeComponents = Array.isArray(partGeometries) && partGeometries.length
        ? partGeometries
            .filter((geometry) => geometry?.alpha?.length)
            .map((geometry, index) => ({
                alpha: geometry.alpha,
                pixelCount: geometry.pixelCount,
                bbox: geometry.bbox,
                centroid: geometry.centroid,
                componentKey: String(geometry?.layer?.partKey || geometry?.layer?.item?.id || `part-${index + 1}`),
                partKeys: [String(geometry?.layer?.partKey || "")].filter(Boolean),
            }))
        : garmentComponents.map((component, index) => ({
            ...component,
            componentKey: `garment-${index + 1}`,
            partKeys: [],
        }));
    if (!runtimeComponents.length || !cuts.length) {
        return { components: runtimeComponents, zones: [], renderedCuts: [] };
    }
    const renderedCuts = [];
    const zones = [];

    runtimeComponents.forEach((component) => {
        const componentKey = String(component?.componentKey || "garment");
        const componentCuts = cuts
            .map((cut) => {
                const rendered = buildRenderedCut({
                    cut,
                    side,
                    groupKey: "garment",
                    componentKey,
                    component,
                    width,
                    height,
                    mirrorX: false,
                });
                if (doesStrokeIntersectComponent({
                    points: rendered.points_norm,
                    component,
                    width,
                    height,
                })) {
                    return rendered;
                }
                return null;
            })
            .filter(Boolean);

        renderedCuts.push(...componentCuts);
        const rawZones = buildComponentZoneMasks({
            component,
            transformedCuts: componentCuts,
            width,
            height,
            minZonePixelCount: Math.max(90, Math.round(width * height * 0.0012)),
        });

        sortZonesTopToBottom(rawZones).forEach((zone, index) => {
            const safeComponentKey = componentKey.replace(/[^a-zA-Z0-9_-]+/g, "-");
            const id = `garment-${side}-${safeComponentKey}-zone-${index + 1}`;
            zones.push({
                ...zone,
                id,
                side,
                targetGroup: "free",
                componentKey,
                partKeys: component.partKeys?.length ? component.partKeys : resolveZonePartKeys(zone, partGeometries),
                label: rawZones.length === 2
                    ? (index === 0 ? "Superior" : "Inferior")
                    : `Zona ${index + 1}`,
                adjacentCutIds: resolveAdjacentCutIdsForZone({
                    zone,
                    renderedCuts: componentCuts,
                    width,
                    height,
                }),
                color: resolveStoredColor(customCutZoneColors?.[id]),
                maskCanvas: buildZoneCanvas(zone, width, height),
            });
        });
    });

    return {
        components: garmentComponents,
        zones,
        renderedCuts,
    };
};

const buildGroupAlphaFromLayers = async ({ layers = [], width, height }) => {
    if (!layers.length || width <= 1 || height <= 1 || typeof document === "undefined") {
        return new Uint8ClampedArray(width * height);
    }
    const canvas = createAlphaCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return new Uint8ClampedArray(width * height);
    for (const layer of layers) {
        const src = String(layer?.img || "");
        if (!src) continue;
        const image = await loadCachedCanvasImage(src);
        if (!image) continue;
        drawContainImage(ctx, image, width, height);
    }
    return extractAlphaMap(ctx, width, height);
};

const buildLayerGeometry = async ({ layer = null, width, height }) => {
    const src = String(layer?.img || "");
    if (!src || width <= 1 || height <= 1 || typeof document === "undefined") return null;
    const image = await loadCachedCanvasImage(src);
    if (!image) return null;
    const canvas = createAlphaCanvas(width, height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    drawContainImage(ctx, image, width, height);
    const alpha = extractAlphaMap(ctx, width, height);
    const metrics = computeAlphaMetrics(alpha, width, height);
    if (!metrics) return null;
    return {
        layer,
        alpha,
        ...metrics,
    };
};

const inferSleeveLayersFromGeometry = async ({ layers = [], width, height }) => {
    if (!Array.isArray(layers) || layers.length < 2) return [];
    const geometries = (await Promise.all(
        layers.map((layer) => buildLayerGeometry({ layer, width, height }))
    )).filter(Boolean);
    if (geometries.length < 2) return [];

    const union = geometries.reduce((acc, geometry) => {
        const bbox = geometry.bbox;
        return {
            minX: Math.min(acc.minX, bbox.minX),
            minY: Math.min(acc.minY, bbox.minY),
            maxX: Math.max(acc.maxX, bbox.maxX),
            maxY: Math.max(acc.maxY, bbox.maxY),
        };
    }, { minX: width, minY: height, maxX: 0, maxY: 0 });
    const unionWidth = Math.max(1, (union.maxX - union.minX) + 1);
    const unionHeight = Math.max(1, (union.maxY - union.minY) + 1);

    const scored = geometries.map((geometry) => {
        const cxNorm = (geometry.centroid.x - union.minX) / unionWidth;
        const cyNorm = (geometry.centroid.y - union.minY) / unionHeight;
        const outerness = Math.abs(cxNorm - 0.5) * 2;
        const upperBias = 1 - cyNorm;
        const aspect = geometry.bbox.height / Math.max(1, geometry.bbox.width);
        const heightRatio = geometry.bbox.height / unionHeight;
        const widthRatio = geometry.bbox.width / unionWidth;
        const shapeBias = clamp(aspect / 1.2, 0, 1.6);
        const slimBias = clamp((0.42 - widthRatio) * 3.4, -0.6, 1.2);
        const verticalBias = clamp((heightRatio - 0.18) * 3.2, -0.6, 1.2);
        const score = (outerness * 2.4) + (upperBias * 0.8) + shapeBias + slimBias + verticalBias;
        return {
            geometry,
            cxNorm,
            score,
        };
    });

    const chooseBest = (side) => {
        const candidates = scored
            .filter((entry) => side === "left" ? entry.cxNorm < 0.46 : entry.cxNorm > 0.54)
            .sort((left, right) => right.score - left.score);
        const best = candidates[0];
        if (!best || best.score < 1.8) return null;
        return best.geometry.layer;
    };

    const leftLayer = chooseBest("left");
    const rightLayer = chooseBest("right");
    return [leftLayer, rightLayer].filter(Boolean);
};

const inferBodyLayersFromGeometry = async ({ layers = [], sleeveLayers = [], width, height }) => {
    const sleeveKeys = new Set((Array.isArray(sleeveLayers) ? sleeveLayers : []).map((layer) => String(layer?.partKey || layer?.img || "")));
    const candidates = (Array.isArray(layers) ? layers : []).filter((layer) => !sleeveKeys.has(String(layer?.partKey || layer?.img || "")));
    if (!candidates.length) return [];
    const geometries = (await Promise.all(
        candidates.map((layer) => buildLayerGeometry({ layer, width, height }))
    )).filter(Boolean);
    if (!geometries.length) return [];
    const ranked = geometries
        .map((geometry) => {
            const cxNorm = geometry.centroid.x / Math.max(1, width);
            const cyNorm = geometry.centroid.y / Math.max(1, height);
            const widthRatio = geometry.bbox.width / Math.max(1, width);
            const heightRatio = geometry.bbox.height / Math.max(1, height);
            const areaRatio = geometry.pixelCount / Math.max(1, width * height);
            const centrality = 1 - Math.min(1, Math.abs(cxNorm - 0.5) * 1.9);
            const verticalBias = 1 - Math.min(1, Math.abs(cyNorm - 0.58) * 1.2);
            const score = (areaRatio * 4.8) + (widthRatio * 2.4) + (heightRatio * 1.2) + centrality + verticalBias;
            return {
                geometry,
                score,
            };
        })
        .sort((left, right) => right.score - left.score);
    const best = ranked[0];
    if (!best) return [];
    return ranked
        .filter((entry, index) => index === 0 || entry.score >= (best.score * 0.84))
        .slice(0, 2)
        .map((entry) => entry.geometry.layer)
        .filter(Boolean);
};

const buildSleeveComponents = (components = []) =>
    [...components]
        .sort((left, right) => Number(left?.centroid?.x || 0) - Number(right?.centroid?.x || 0))
        .map((component, index) => ({
            ...component,
            slot: index === 0 ? "left" : "right",
        }));

export const buildCustomCutRuntime = async ({
    layers = [],
    side = "front",
    width = 0,
    height = 0,
    customCutsBySide = null,
    customCutZoneColors = {},
}) => {
    const safeSide = side === "back" ? "back" : "front";
    if (width <= 1 || height <= 1 || typeof document === "undefined") {
        return {
            side: safeSide,
            zones: [],
            zonesById: {},
            renderedCuts: [],
            groups: {
                garment: { components: [], zones: [], renderedCuts: [] },
                body: { component: null, zones: [], renderedCuts: [] },
                sleeve: { components: [], zones: [], renderedCuts: [] },
            },
        };
    }

    const cuts = (Array.isArray(customCutsBySide?.[safeSide]) ? customCutsBySide[safeSide] : [])
        .map((cut) => normalizeCustomCutStroke(cut))
        .filter((cut) => Array.isArray(cut.points_norm) && cut.points_norm.length >= 2);

    const maskLayers = (layers || []).filter((layer) => layer.kind === "mask" && layer?.img);
    const [partGeometries, garmentAlpha] = await Promise.all([
        Promise.all(maskLayers.map((layer) => buildLayerGeometry({ layer, width, height }))),
        buildGroupAlphaFromLayers({ layers: maskLayers, width, height }),
    ]);

    const garmentComponents = computeConnectedComponents({
        alpha: garmentAlpha,
        width,
        height,
        minPixelCount: Math.max(120, Math.round(width * height * 0.0022)),
    });
    const garmentMaskCanvas = buildZoneCanvas({ alpha: garmentAlpha }, width, height);
    const garmentRuntime = buildGarmentRuntime({
        garmentComponents,
        side: safeSide,
        cuts,
        partGeometries: partGeometries.filter(Boolean),
        width,
        height,
        customCutZoneColors,
    });
    const zones = [...garmentRuntime.zones];
    const zonesById = Object.fromEntries(zones.map((zone) => [String(zone.id), zone]));
    const renderedCuts = [...garmentRuntime.renderedCuts];

    return {
        side: safeSide,
        width,
        height,
        garmentMaskCanvas,
        zones,
        zonesById,
        renderedCuts,
        groups: {
            garment: garmentRuntime,
            body: { component: null, zones: [], renderedCuts: [] },
            sleeve: { components: [], zones: [], renderedCuts: [] },
        },
    };
};

export const drawCustomCutRuntime = ({
    ctx,
    runtime = null,
    width = 0,
    height = 0,
    drawFills = true,
    drawLines = true,
}) => {
    if (!ctx || !runtime || width <= 1 || height <= 1) return;
    if (drawFills) {
        (runtime.zones || []).forEach((zone) => {
            if (!zone?.color?.code || !zone?.maskCanvas) return;
            const fillCanvas = createAlphaCanvas(width, height);
            const fillCtx = fillCanvas.getContext("2d");
            if (!fillCtx) return;
            fillCtx.drawImage(zone.maskCanvas, 0, 0, width, height);
            fillCtx.globalCompositeOperation = "source-in";
            fillCtx.fillStyle = zone.color.code;
            fillCtx.fillRect(0, 0, width, height);
            fillCtx.globalCompositeOperation = "source-over";
            ctx.drawImage(fillCanvas, 0, 0, width, height);
        });
    }
    if (drawLines) {
        const lineCanvas = createAlphaCanvas(width, height);
        const lineCtx = lineCanvas.getContext("2d");
        if (lineCtx) {
            (runtime.renderedCuts || []).forEach((cut) => {
                drawCustomCutStroke(lineCtx, cut, width, height, {
                    color: "rgba(59, 59, 59, 0.96)",
                    lineWidth: Math.max(1.9, 2.15 * (width / 420)),
                    dashed: true,
                });
            });
            if (runtime?.garmentMaskCanvas) {
                lineCtx.globalCompositeOperation = "destination-in";
                lineCtx.drawImage(runtime.garmentMaskCanvas, 0, 0, width, height);
                lineCtx.globalCompositeOperation = "source-over";
            }
            ctx.drawImage(lineCanvas, 0, 0);
        }
    }
};

const distancePointToSegment = (point, start, end) => {
    const [px, py] = point;
    const [ax, ay] = start;
    const [bx, by] = end;
    const abx = bx - ax;
    const aby = by - ay;
    const abLenSq = (abx * abx) + (aby * aby);
    if (abLenSq <= 0.000001) {
        return Math.hypot(px - ax, py - ay);
    }
    const t = clamp((((px - ax) * abx) + ((py - ay) * aby)) / abLenSq, 0, 1);
    const projX = ax + (abx * t);
    const projY = ay + (aby * t);
    return Math.hypot(px - projX, py - projY);
};

export const findNearestCustomCutAtPoint = (renderedCuts = [], point = []) => {
    if (!Array.isArray(renderedCuts) || renderedCuts.length === 0 || !Array.isArray(point) || point.length < 2) {
        return null;
    }
    let best = null;
    renderedCuts.forEach((cut) => {
        const points = Array.isArray(cut?.points_norm) ? cut.points_norm : [];
        if (points.length < 2) return;
        for (let index = 1; index < points.length; index += 1) {
            const distance = distancePointToSegment(point, points[index - 1], points[index]);
            if (!best || distance < best.distance) {
                best = {
                    cut,
                    distance,
                };
            }
        }
    });
    if (!best || best.distance > 0.05) return null;
    return best;
};

export const createRelativeCustomCutFromStagePoints = ({
    id = null,
    tool = "line",
    stagePoints = [],
    stageWidth = 0,
    stageHeight = 0,
    targetGroup = "body",
    component = null,
    anchorComponent = null,
}) => {
    if (!Array.isArray(stagePoints) || stagePoints.length < 2) return null;
    const resolvedId = String(id || `cut-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const bbox = component?.bbox || null;
    const normalizedStagePoints = stagePoints.map((point) => ([
        clamp(Number(point?.[0] || 0) / Math.max(1, stageWidth || 1), 0, 1),
        clamp(Number(point?.[1] || 0) / Math.max(1, stageHeight || 1), 0, 1),
    ]));
    const normalizedPoints = stagePoints.map((point) => {
        if (!bbox) {
            return [
                clamp(Number(point?.[0] || 0) / Math.max(1, stageWidth || 1), 0, 1),
                clamp(Number(point?.[1] || 0) / Math.max(1, stageHeight || 1), 0, 1),
            ];
        }
        const x = clamp((Number(point?.[0] || 0) - bbox.minX) / Math.max(1, bbox.width), 0, 1);
        const y = clamp((Number(point?.[1] || 0) - bbox.minY) / Math.max(1, bbox.height), 0, 1);
        return [x, y];
    });
    return normalizeCustomCutStroke({
        id: resolvedId,
        tool,
        target_group: targetGroup,
        points_norm: normalizedPoints,
        stage_points_norm: normalizedStagePoints,
        anchor_component: targetGroup === "sleeve" ? (anchorComponent || "left") : "body",
        mirror_mode: "paired-sides",
        mirror_group_id: resolvedId,
        mirrored_from_side: null,
    });
};

export const translateRelativeCustomCut = ({
    cut,
    deltaX = 0,
    deltaY = 0,
    component = null,
    stageWidth = 0,
    stageHeight = 0,
}) => {
    if (!cut) return cut;
    if (Array.isArray(cut?.stage_points_norm) && cut.stage_points_norm.length >= 2) {
        const dxStage = Number(deltaX || 0) / Math.max(1, stageWidth || 1);
        const dyStage = Number(deltaY || 0) / Math.max(1, stageHeight || 1);
        return normalizeCustomCutStroke({
            ...cut,
            stage_points_norm: cut.stage_points_norm.map((point) => [
                clamp(Number(point?.[0] || 0) + dxStage, 0, 1),
                clamp(Number(point?.[1] || 0) + dyStage, 0, 1),
            ]),
        });
    }
    if (!component?.bbox) return cut;
    const dx = Number(deltaX || 0) / Math.max(1, component.bbox.width);
    const dy = Number(deltaY || 0) / Math.max(1, component.bbox.height);
    return normalizeCustomCutStroke({
        ...cut,
        points_norm: (Array.isArray(cut.points_norm) ? cut.points_norm : []).map((point) => [
            clamp(Number(point?.[0] || 0) + dx, 0, 1),
            clamp(Number(point?.[1] || 0) + dy, 0, 1),
        ]),
    });
};

export const resolveCustomCutRuntimeComponent = ({
    runtime = null,
    targetGroup = "free",
    point = null,
    preferredSlot = null,
}) => {
    if (!runtime) return null;
    const components = Array.isArray(runtime?.groups?.garment?.components) ? runtime.groups.garment.components : [];
    if (!components.length) return null;
    if (preferredSlot) {
        const exact = components.find((component) => String(component.slot || component.componentKey || "") === String(preferredSlot));
        if (exact) return exact;
    }
    if (Array.isArray(point) && point.length >= 2) {
        const px = Number(point[0] || 0);
        const py = Number(point[1] || 0);
        const hit = components.find((component) => {
            const x = clamp(Math.round(px), 0, runtime.width - 1);
            const y = clamp(Math.round(py), 0, runtime.height - 1);
            const idx = (y * runtime.width) + x;
            return component.alpha?.[idx] > 0;
        });
        if (hit) return hit;
    }
    return components[0] || null;
};

export const resolveCustomCutTargetGroupLabel = (group = "") => {
    const normalized = String(group || "").toLowerCase();
    if (normalized === "sleeve") return "Mangas";
    if (normalized === "body") return "Cuerpo";
    return "la prenda";
};

export const summarizeCustomCuts = (customCutsBySide = null) => {
    const byId = new Set();
    ["front", "back"].forEach((side) => {
        const list = Array.isArray(customCutsBySide?.[side]) ? customCutsBySide[side] : [];
        list.forEach((cut) => {
            const id = String(cut?.id || "").trim();
            if (id) byId.add(id);
        });
    });
    return byId.size;
};
